import React, { MouseEventHandler, useReducer, useRef } from 'react';
import componentProps, { ComponentProps } from '../../../../../helpers/componentProps';
import useOnClickOutside from '../../../../../hooks/useOnClickOutside';
import { applyComponentTheme } from '../../../../../theme';
import Collapsible from '../../../../Collapsible';
import Icon from '../../../../Icon';
import {
  ButtonWrapper,
  ToggleTooltipButton,
  TooltipBody,
  TooltipWrapper,
} from './styles';

export type TooltipProps = {
  body: string;
  buttonText: {
    open: string;
    close: string;
  };
  onExpand?: MouseEventHandler;
  onCollapse?: MouseEventHandler;
  onLinkClick?: MouseEventHandler;
  onClickOutside?: (event: MouseEvent) => void;
  initiallyShowTooltip?: boolean | undefined;
  tooltipToggleId?: string;
  icon?: string;
} & ComponentProps;

const Tooltip: React.FC<TooltipProps> = ({
  body,
  buttonText,
  onExpand,
  onCollapse,
  onLinkClick,
  onClickOutside,
  initiallyShowTooltip,
  tooltipToggleId,
  id,
  icon,
  ...props
}) => {
  const ref = useRef(null);
  const [tooltipOpen, toggleTooltip] = useReducer(
    (state) => !state,
    !!initiallyShowTooltip
  );

  const onClick: MouseEventHandler = (event) => {
    if (onExpand && !tooltipOpen) {
      onExpand(event);
    }
    if (onCollapse && tooltipOpen) {
      onCollapse(event);
    }
    toggleTooltip();
  };

  useOnClickOutside(ref, onClickOutside);

  return (
    <span {...componentProps(props)} ref={ref}>
      <ButtonWrapper>
        {icon && <Icon name={icon} size="medium" />}
        <ToggleTooltipButton
          onClick={onClick}
          aria-describedby={tooltipOpen ? id : undefined}
          aria-expanded={tooltipOpen ? 'true' : 'false'}
          aria-label={tooltipOpen ? buttonText.close : buttonText.open}
          id={tooltipToggleId}>
          {tooltipOpen ? buttonText.close : buttonText.open}
        </ToggleTooltipButton>
      </ButtonWrapper>
      <Collapsible isOpened={tooltipOpen} role="tooltip" id={id}>
        <TooltipWrapper onClick={onLinkClick}>
          <TooltipBody html={body} />
        </TooltipWrapper>
      </Collapsible>
    </span>
  );
};

export default applyComponentTheme(Tooltip, 'Tooltip');
